import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgbCollapseModule, NgbModule, NgbPopoverModule, NgbProgressbarModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ChartsModule } from '@progress/kendo-angular-charts';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { WindowModule } from '@progress/kendo-angular-dialog';
import { ComboBoxModule, DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { FilterModule } from '@progress/kendo-angular-filter';
import { ExcelModule, GridModule } from '@progress/kendo-angular-grid';
import { ForecastRxTreelistModule } from './forecast-rxtreelist/forecast-rxtreelist.module';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IntlModule } from '@progress/kendo-angular-intl';
import { LabelModule } from '@progress/kendo-angular-label';
import { CoreModule } from 'src/app/core/core.module';
import { CreateAddressModalComponent } from 'src/app/core/services/modal/create-address-modal/create-address-modal.component';
import { CreatePoWizardModalComponent } from 'src/app/core/services/modal/create-po-wizard-modal/create-po-wizard-modal.component';
import { CreateSupplierModalComponent } from 'src/app/core/services/modal/create-supplier-modal/create-supplier-modal.component';
import { InputFormModalComponent } from 'src/app/core/services/modal/input-form-modal/input-form-modal.component';
import { CompleteComponent } from 'src/app/routes/dashboard/shipments/steps/complete/complete.component';

import { ShipmentPickListViewComponent } from '../../../routes/dashboard/shipments/manage-shipments/shipment-pick-list-view/shipment-pick-list-view.component';
import { DataAddTextUnitToNumberPipe } from '../components/data-table/data-add-text-to-number.pipe';
import { DataFlagClassPipe } from '../components/data-table/data-flag-class.pipe';
import { AlphabeticalColumnChooserComponent } from './forecast-rxdata-table/alphabetical-column-chooser.component';
import { DataGetMaxLengthPipe } from '../components/data-table/data-get-max-length.pipe';
import { DataGetMaxPipe } from '../components/data-table/data-get-max.pipe';
import { DataGetMinPipe } from '../components/data-table/data-get-min.pipe';
import { DataGridClassPipe } from '../components/data-table/data-grid-class.pipe';
import { DataIsFilterMenuColumnsPipe } from '../components/data-table/data-is-filter-menu-columns.pipe';
import { DataIsSortMenuColumnsPipe } from '../components/data-table/data-is-sort-menu-columns.pipe';
import { DataIsSummaryByVendorPipe } from '../components/data-table/data-is-summary-by-vendor.pipe';
import { DataOutputPipe } from '../components/data-table/data-output.pipe';
import { DataRelativeTimePipe } from '../components/data-table/data-relative-time.pipe';
import { DataReorderColumnPipe } from '../components/data-table/data-reorder-column.pipe';
import { DataResolveFilterTypePipe } from '../components/data-table/data-resolve-filter-type.pipe';
import { DataSetWidthColumnPipe } from '../components/data-table/data-set-width-column.pipe';
import { DataTooltipPipe } from '../components/data-table/data-tooltip.pipe';
import { DataTransformBooleanPipe } from '../components/data-table/data-transform-boolean.pipe';
import { DataTransformOversizePipe } from '../components/data-table/data-transform-oversize.pipe';
import { DecimalNumberDirective } from '../components/decimal-number/decimal-number.directive';
import { CompareChangesComponent } from './comparison-changes/comparison-changes.component';
import { CompanyDropdownComponent } from './company-dropdown/company-dropdown.component';
import { CopyHistoryModalComponent } from './copy-history-modal/copy-history-modal.component';
import { CreateBomModalComponent } from './create-bom-modal/create-bom-modal.component';
import { EditTagsModalComponent } from './edit-tags-modal/edit-tags-modal.component';
import { EstimatedMarginCalculationModalComponent } from './estimated-margin-calculation-modal/estimated-margin-calculation-modal.component';
import { FieldGroupDataTableComponent } from './field-group-data-table/field-group-data-table.component';
import { FileUploadModalComponent } from './file-upload-modal/file-upload-modal.component';
import { ForecastRXDataTableComponent } from './forecast-rxdata-table/forecast-rxdata-table.component';
import { HISTORY_LOGS_UI } from './forecast-rxdata-table/history-logs-ui';
import { GeneralWarningModalModule } from './general-warning-modal/general-warning-modal.module';
import { GenericDataTableComponent } from './generic-data-table/generic-data-table.component';
import { GenericItemModalComponent } from './generic-item-modal/generic-item-modal.component';
import { GenericKendoGridComponent } from './generic-kendo-grid/generic-kendo-grid.component';
import { GridItemLookupComponent } from './grid-item-lookup/grid-item-lookup.component';
import { GridViewComboboxComponent } from './grid-view-combobox/grid-view-combobox.component';
import { HistoryDataTableReducedComponent } from './history-data-table-reduced/history-data-table-reduced.component';
import { HistoryDataTableComponent } from './history-data-table/history-data-table.component';
import { ImportFailModalComponent } from './import-fail-modal/import-fail-modal.component';
import { InboundAnalyzerModalComponent } from './inbound-analyzer-modal/inbound-analyzer-modal.component';
import { InventoryStatusBadgeComponent } from './inventory-status-badge/inventory-status-badge.component';
import { ItemCardDetailComponent } from './item-card-detail/item-card-detail.component';
import { ListInputComponent } from './list-input/list-input.component';
import { LookupInputComponent } from './lookup-input/lookup-input.component';
import { LookupItemComponent } from './lookup-item/lookup-item.component';
import { LostSaleTrackingComponent } from './lost-sale-tracking/lost-sale-tracking.component';
import { NoticeToResyncModalComponent } from './notice-to-resync-modal/notice-to-resync-modal.component';
import { PIPES } from './pipes';
import { PopoverComponent } from './popover/popover.component';
import { PrintStickersModalComponent } from './print-stickers-modal/print-stickers-modal.component';
import { RepricingBulkSettingModalComponent } from './repricing-bulk-setting-modal/repricing-bulk-setting-modal.component';
import { RepricingSettingModalComponent } from './repricing-setting-modal/repricing-setting-modal.component';
import { SalesVelocityModalComponent } from './restock-settings/restock-settings.component';
import { SaleVelocityCalculationComponent } from './sale-velocity-calculation/sale-velocity-calculation.component';
import { VendorHiddenModalComponent } from './vendor-hidden-modal/vendor-hidden-modal.component';
import { ViewOfHistoryComponent } from './view-of-history/view-of-history.component';
import { DiscountTagComponent } from './discount-tag/discount-tag.component';
import { RepricingInventoryHealthComponent } from './repricing-inventory-health/repricing-inventory-health.component';
import { RepricingRecommendAnalysisContentComponent } from './repricing-recommend-analysis-content/repricing-recommend-analysis-content.component';
import { ProductAdInventoryHealthComponent } from './product-ad-inventory-health/product-ad-inventory-health.component';
import { ProductAdInventoryAnalysisModalComponent } from './product-ad-inventory-analysis-modal/product-ad-inventory-analysis-modal.component';
import { MathFormulaModule } from '../math-formula/math-formula.module';
import { DateRangeSelectionModule } from './date-range-selection/date-range-selection.module';
import { ItemPricingInventoryHealthChipModule } from './item-pricing-inventory-health-chip/item-pricing-inventory-health-chip.module';
import { RepricingTrendBadgeComponent } from './repricing-trend-badge/repricing-trend-badge.component';
import { RepricingPriceEditorComponent } from './repricing-price-editor/repricing-price-editor.component';
import { PriceUploadErrorModalComponent } from './price-upload-error-modal/price-upload-error-modal.component';
import { AutomationActivationModalComponent } from './automation-activation-modal/automation-activation-modal.component';
import { ExcelUploadErrorModalComponent } from './excel-upload-error-modal/excel-upload-error-modal.component';
import { KendoSelectFilterModule } from './kendo-select-filter/kendo-select-filter.module';
import { InventoryConditionTableComponent } from './inventory-condition-table/inventory-condition-table.component';
import { IconModule } from '../components/icon/icon.module';

@NgModule({
  declarations: [
    HISTORY_LOGS_UI,
    PIPES,
    CreateSupplierModalComponent,
    CreatePoWizardModalComponent,
    InputFormModalComponent,
    DataOutputPipe,
    DataAddTextUnitToNumberPipe,
    DataTransformOversizePipe,
    DataResolveFilterTypePipe,
    DataTransformBooleanPipe,
    DataGetMaxPipe,
    DataIsSummaryByVendorPipe,
    DataTooltipPipe,
    DataFlagClassPipe,
    DataRelativeTimePipe,
    DataGetMinPipe,
    DataGetMaxLengthPipe,
    DataIsSortMenuColumnsPipe,
    DataIsFilterMenuColumnsPipe,
    DataSetWidthColumnPipe,
    DataGridClassPipe,
    DataReorderColumnPipe,
    ForecastRXDataTableComponent,
    AlphabeticalColumnChooserComponent,
    FileUploadModalComponent,
    LookupInputComponent,
    ItemCardDetailComponent,
    GenericDataTableComponent,
    GenericKendoGridComponent,
    HistoryDataTableReducedComponent,
    LookupItemComponent,
    HistoryDataTableComponent,
    CopyHistoryModalComponent,
    DecimalNumberDirective,
    ImportFailModalComponent,
    VendorHiddenModalComponent,
    GridViewComboboxComponent,
    ListInputComponent,
    NoticeToResyncModalComponent,
    ViewOfHistoryComponent,
    PrintStickersModalComponent,
    GridItemLookupComponent,
    ShipmentPickListViewComponent,
    FieldGroupDataTableComponent,
    SalesVelocityModalComponent,
    SaleVelocityCalculationComponent,
    EstimatedMarginCalculationModalComponent,
    GenericItemModalComponent,
    EditTagsModalComponent,
    PopoverComponent,
    InboundAnalyzerModalComponent,
    CompanyDropdownComponent,
    CreateAddressModalComponent,
    CompleteComponent,
    CreateBomModalComponent,
    LostSaleTrackingComponent,
    RepricingSettingModalComponent,
    RepricingBulkSettingModalComponent,
    CompareChangesComponent,
    InventoryStatusBadgeComponent,
    DiscountTagComponent,
    RepricingInventoryHealthComponent,
    RepricingRecommendAnalysisContentComponent,
    RepricingTrendBadgeComponent,
    RepricingPriceEditorComponent,
    ProductAdInventoryHealthComponent,
    ProductAdInventoryAnalysisModalComponent,
    PriceUploadErrorModalComponent,
    AutomationActivationModalComponent,
    ExcelUploadErrorModalComponent,
    InventoryConditionTableComponent,
  ],
  exports: [
    CreateAddressModalComponent,
    CreateSupplierModalComponent,
    CreatePoWizardModalComponent,
    InputFormModalComponent,
    ForecastRXDataTableComponent,
    FieldGroupDataTableComponent,
    LookupInputComponent,
    ItemCardDetailComponent,
    LookupItemComponent,
    HistoryDataTableComponent,
    DecimalNumberDirective,
    ListInputComponent,
    GridItemLookupComponent,
    SalesVelocityModalComponent,
    GridViewComboboxComponent,
    CompanyDropdownComponent,
    CoreModule,
    CompleteComponent,
    CreateBomModalComponent,
    LostSaleTrackingComponent,
    RepricingSettingModalComponent,
    RepricingBulkSettingModalComponent,
    GeneralWarningModalModule,
    CompareChangesComponent,
    InventoryStatusBadgeComponent,
    DiscountTagComponent,
    RepricingInventoryHealthComponent,
    RepricingRecommendAnalysisContentComponent,
    DateRangeSelectionModule,
    ItemPricingInventoryHealthChipModule,
    RepricingTrendBadgeComponent,
    RepricingPriceEditorComponent,
    ProductAdInventoryHealthComponent,
    ProductAdInventoryAnalysisModalComponent,
    PriceUploadErrorModalComponent,
    AutomationActivationModalComponent,
    ExcelUploadErrorModalComponent,
    ForecastRxTreelistModule,
    InventoryConditionTableComponent,
    ViewOfHistoryComponent,
  ],
  imports: [
    GeneralWarningModalModule,
    ItemPricingInventoryHealthChipModule,
    CommonModule,
    CoreModule,
    RouterModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    GridModule,
    ComboBoxModule,
    DropDownsModule,
    NgbPopoverModule,
    NgbProgressbarModule,
    NgbCollapseModule,
    NgbTooltipModule,
    NgbModule,
    ExcelModule,
    ChartsModule,
    ButtonsModule,
    DateInputsModule,
    IntlModule,
    LabelModule,
    InputsModule,
    WindowModule,
    FilterModule,
    MathFormulaModule,
    IconModule,
    ForecastRxTreelistModule,
    DateRangeSelectionModule,
    KendoSelectFilterModule,
  ],
  bootstrap: [
    CreateAddressModalComponent,
    RepricingSettingModalComponent,
    RepricingBulkSettingModalComponent,
    CreateSupplierModalComponent,
    CreatePoWizardModalComponent,
    InputFormModalComponent,
    ForecastRXDataTableComponent,
    FieldGroupDataTableComponent,
    ProductAdInventoryAnalysisModalComponent,
    PriceUploadErrorModalComponent,
    ExcelUploadErrorModalComponent
  ]
})
export class ForecastuiModule {}
